'use client';

import { cn } from '@heidi/utils';

/**
 * Full-width footer button that grows instead of clipping.
 *
 * `Button` is `whitespace-nowrap` at a fixed `h-10`, so a long German or French
 * label overflows its own box in the 400px content column; these overrides let
 * it wrap onto a second line and take the height it needs.
 */
export const WRAPPING_FOOTER_BUTTON_CLASS =
  'h-auto min-h-10 w-full whitespace-normal text-center';

interface OnboardingStepShellProps {
  /** Step content. Scrolls when it outgrows the column. */
  children: React.ReactNode;
  /** Actions kept on-screen below the scroll region. */
  footer: React.ReactNode;
  /** Extra classes for the scroll region — usually the content `gap-*`. */
  contentClassName?: string;
  /**
   * Drops the rule above the footer. Only for steps whose content is short
   * enough that nothing ever scrolls under it, where the rule reads as a stray
   * divider rather than a scroll edge.
   */
  hideFooterDivider?: boolean;
  /**
   * Renders the root as a `<form>` so a `type="submit"` footer button and
   * Enter-to-submit keep working on the data-collection steps.
   */
  onSubmit?: (event: React.FormEvent) => void;
  /** The scroll region, for steps that drive it (see `useAutoScrollToBottom`). */
  scrollRef?: React.Ref<HTMLDivElement>;
}

/**
 * Step scaffold that keeps the footer actions reachable without scrolling.
 *
 * Content scrolls inside a bounded region and the footer sits below it as a
 * real element rather than a `sticky` overlay, so Continue/Skip stay visible
 * on a short viewport without covering the content behind them.
 *
 * Only works when the layout column is height-bounded — the step must be
 * listed in `STEPS_WITH_OWN_SCROLL` in `PersonalizationLayout`, otherwise the
 * column grows instead of scrolling and the footer drops below the fold.
 */
export function OnboardingStepShell({
  children,
  footer,
  contentClassName,
  hideFooterDivider = false,
  onSubmit,
  scrollRef,
}: OnboardingStepShellProps) {
  const Root = onSubmit ? 'form' : 'div';
  return (
    <Root className="flex min-h-0 flex-1 flex-col" {...(onSubmit ? { onSubmit } : {})}>
      <div
        ref={scrollRef}
        className={cn(
          // `px-2 -mx-2` is focus-ring breathing room, not spacing: `overflow-y`
          // makes `overflow-x` compute to `auto` too, which cropped the inputs'
          // outset ring flat against both edges of the column. The padding gives
          // the halo room and the negative margin gives it back, so content
          // stays put — the idiom the design tokens prescribe for a scroll
          // container (see "Focus rings" in design-tokens.md).
          '-mx-2 flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-4',
          contentClassName
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          'shrink-0 bg-surface-accent pt-3 [padding-bottom:max(env(safe-area-inset-bottom),0.5rem)]',
          !hideFooterDivider && 'border-t border-border'
        )}
      >
        {footer}
      </div>
    </Root>
  );
}
